import { ICell } from 'rewire-grid';
import { IShift, IJob } from '../DataTypes';
import scheduleModel, { ScheduleCellType } from './ScheduleModel';
import infoPanelEmployeeViewModel from './InfoPanelEmployeeViewModel';
import infoPanelJobViewModel from './InfoPanelJobViewModel';
import infoPanelShiftViewModel from './InfoPanelShiftViewModel';
// import infoPanelMultiJobViewModel from './InfoPanelMultiJobViewModel';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import { Deferred } from '../utility/Deferred';

export function waitForMouseButtonRelease(isMouseDown: () => boolean, callback: () => void): void {
  if (!isMouseDown() || typeof document === 'undefined') {
    callback?.();
    return;
  }

  const handleMouseUp = () => {
    document.removeEventListener('mouseup', handleMouseUp, true);
    callback();
  };

  document.addEventListener('mouseup', handleMouseUp, true);
}

let _waitingForMouseRelease: Deferred<void> | undefined;

export class ScheduleInfoPanelModel {
  constructor() {
    this.load = debounce(this.load, 250, { leading: true, trailing: true }) as any;
  }

  get selectedCells(): ICell[] {
    return scheduleModel.selectedCells;
  }

  get selectedCell(): ICell | undefined {
    return scheduleModel.selectedCell;
  }

  get selectedCellType(): ScheduleCellType | undefined {
    if (!this.selectedCell) return undefined;

    return scheduleModel.getCellType(this.selectedCell);
  }

  get selectedCellsType(): ScheduleCellType | undefined {
    let selectedCellsType: ScheduleCellType | undefined;
    for (let i = 0; i < this.selectedCells.length; i++) {
      const cell = this.selectedCells[i];
      const cellType = scheduleModel.getCellType(cell);
      if (!selectedCellsType) {
        selectedCellsType = cellType;
      } else if (selectedCellsType !== cellType) {
        return undefined;
      }
    }
    return selectedCellsType;
  }

  get isMultiSelect(): boolean {
    return scheduleModel.isMultiSelect;
  }

  get currentInfoPanelModel(): any {
    if (this.selectedCellType === 'Employee') {
      return infoPanelEmployeeViewModel;
    } else if (this.selectedCellType === 'Job') {
      if (!this.isMultiSelect) {
        return infoPanelJobViewModel;
      }
    } else if (this.selectedCellsType === 'Shift') {
      return infoPanelShiftViewModel;
    }

    return undefined;
  }

  get viewOnly(): boolean {
    return scheduleModel.viewOnly;
  }

  reset() {
    if (!this.currentInfoPanelModel) {
      return;
    }

    this.currentInfoPanelModel.reset();
  }

  _previousLoadValue: any = undefined;

  async load() {
    if (!this.currentInfoPanelModel) {
      return;
    }

    if (scheduleModel.grid.isMouseDown) {
      if (_waitingForMouseRelease) {
        return _waitingForMouseRelease.promise;
      }

      waitForMouseButtonRelease(
        () => scheduleModel.grid.isMouseDown,
        () => {
          _waitingForMouseRelease?.resolve();
          _waitingForMouseRelease = undefined;
          requestAnimationFrame(() => {
            if (infoPanelShiftViewModel === this.currentInfoPanelModel && !infoPanelShiftViewModel.isView('details')) {
              infoPanelShiftViewModel.setView('details');
            }
            this.load();
          });
        },
      );

      return;
    }

    if (!this.currentInfoPanelModel || !this.selectedCell) {
      return;
    }

    let loadValue = this.selectedCell!.value;

    if (this.selectedCellsType === 'Shift' && this.isMultiSelect) {
      loadValue = this.selectedCells.map((cell: ICell) => cell.value) as (IShift | undefined)[];
    } else if (this.selectedCellsType === 'Job' && this.isMultiSelect) {
      loadValue = this.selectedCells.map((cell: ICell) => cell.value) as (IJob | undefined)[];
    }

    if (loadValue && this._previousLoadValue) {
      const equals = this._previousLoadValue?.length === loadValue?.length && isEqual(this._previousLoadValue, loadValue);
      if (equals) {
        return;
      }
    }

    await this.currentInfoPanelModel.load(loadValue);
    this._previousLoadValue = loadValue;
  }

  submit = async () => {
    if (this.selectedCell) {
      // restore focus to selected grid cell on save.
      this.selectedCell.setFocus();
    }
    return false;
  };
}

const scheduleInfoPanelModel = new ScheduleInfoPanelModel();
export default scheduleInfoPanelModel;
